import { InputCopy } from "@/components/molecules/input-copy";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { ProvisionResult } from "@/features/admin";
import { CheckCircle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Credentials hand-over panel. The password is only ever returned once by the API (it is
 * bcrypt-hashed server-side), so this is the single opportunity to copy it.
 */
export function CredentialsCard({ result }: { result: ProvisionResult }) {
  const summary = [
    `Espace : ${result.client.name}`,
    `URL : ${result.login_url}`,
    `Email : ${result.user.email}`,
    result.password ? `Mot de passe : ${result.password}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Card className="border-emerald-500/40">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle
            className="h-5 w-5 text-emerald-600 dark:text-emerald-400"
            weight="fill"
          />
          Accès créé
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {result.client_created
            ? `Nouveau tenant « ${result.client.name} » créé, `
            : `Ajouté au tenant « ${result.client.name} », `}
          {result.user_created
            ? "compte utilisateur créé."
            : "compte utilisateur existant réutilisé."}
        </p>

        <div className="space-y-2">
          <Label>Adresse de connexion</Label>
          <InputCopy value={result.login_url} />
        </div>

        <div className="space-y-2">
          <Label>Email</Label>
          <InputCopy value={result.user.email} />
        </div>

        {result.password ? (
          <div className="space-y-2">
            <Label>Mot de passe</Label>
            <InputCopy value={result.password} />
            <p className="text-xs text-muted-foreground">
              Affiché une seule fois — il n'est pas récupérable ensuite.
            </p>
          </div>
        ) : (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Ce compte avait déjà un mot de passe : il a été conservé. Utilisez «
            Réinitialiser le mot de passe » dans la liste ci-dessous pour en
            générer un nouveau.
          </p>
        )}

        <Button
          variant="outline"
          className="w-full"
          onClick={async () => {
            await navigator.clipboard.writeText(summary);
            toast.success("Informations copiées");
          }}
        >
          <Copy className="h-4 w-4 mr-2" />
          Copier toutes les informations
        </Button>
      </CardContent>
    </Card>
  );
}
